import { Landmark, Smartphone, Zap, CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import type { Mandate } from "@/types/mandate";
import { mandateService } from "@/services/apiServices";
import DemoBanner from "./components/DemoBanner";
import ExpiredCard from "./components/ExpiredCard";
import SimulationStep from "./components/SimulationStep";
import { fmtINR } from "./data";

const MODES = [
  { key: "NACH", title: "eNACH", sub: "Debit from your bank account via net banking, debit card or Aadhaar OTP", icon: Landmark },
  { key: "UPI_AUTO_PAY", title: "UPI Autopay", sub: "Approve in any UPI app", icon: Smartphone },
];

export default function MockAuthPage() {
  const { refNo = "" } = useParams();
  const [mandate, setMandate] = useState<Mandate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    if (!refNo) {
      setLoading(false);
      return;
    }

    mandateService
      .getById(refNo)
      .then((res) => {
        if (isMounted && res.success && res.data?.mandate) {
          const apiM = res.data.mandate;
          setMandate({
            id: apiM.id || `MND-${refNo}`,
            refNo: apiM.refNo || refNo,
            retailerId: apiM.retailerId || "",
            retailerName: apiM.retailerName || "",
            shopName: apiM.shopName || "",
            customerName: apiM.customerName || "",
            customerMobile: apiM.customerMobile || "",
            customerEmail: apiM.customerEmail || "",
            bankName: apiM.bankName || "",
            accountNumber: apiM.accountNumber || "",
            ifsc: apiM.ifscCode || apiM.ifsc || "",
            accountType: apiM.accountType || "Savings",
            authMode: apiM.authMode || "eNACH",
            amount: Number(apiM.amount || apiM.emiAmount || apiM.totalPayable || 0),
            maxAmount: Number(apiM.maxAmount || apiM.totalPayable || apiM.productPrice || 0),
            frequency: apiM.frequency || "Monthly",
            startDate: apiM.startDate || "",
            endDate: apiM.endDate || "",
            purpose: apiM.purpose || "EMI Collection",
            status: apiM.status || "PENDING_AUTH",
            umrn: apiM.umrn || null,
            authLink: `/auth/${refNo}`,
            failureReason: apiM.failureReason || null,
            createdAt: apiM.createdAt || new Date().toISOString(),
            updatedAt: apiM.updatedAt || new Date().toISOString(),
          });
        } else if (isMounted) {
          setMandate(null);
        }
      })
      .catch(() => {
        if (isMounted) setMandate(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [refNo]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page px-6 py-6">
        <div className="flex items-center gap-3 rounded-xl border border-line bg-card p-6 shadow-sm text-ink font-medium">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          <span>Loading Mandate Details...</span>
        </div>
      </div>
    );
  }

  if (!mandate) return <ExpiredCard />;

  return <AuthFlow mandate={mandate} refNo={refNo} />;
}

function AuthFlow({ mandate, refNo }: { mandate: Mandate; refNo: string }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("");
  const [consent, setConsent] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [simulate, setSimulate] = useState(false);
  const [quickAuthorizing, setQuickAuthorizing] = useState(false);

  function handleProceed() {
    setConnecting(true);
    window.setTimeout(() => {
      setConnecting(false);
      setSimulate(true);
    }, 1500);
  }

  async function handleQuickAuthorize() {
    setQuickAuthorizing(true);
    try {
      await mandateService.simulateAuth(refNo, "SUCCESS");
    } catch (err) {
      console.warn("Simulate auth call issue, proceeding with status:", err);
    } finally {
      setQuickAuthorizing(false);
      navigate(`/auth/${refNo}/status?outcome=success`);
    }
  }

  return (
    <div className="flex min-h-screen flex-col justify-center bg-page px-6 py-6">
      <div className="mx-auto flex w-full max-w-[480px] flex-col">
        <DemoBanner />

        {/* Temporary Testing Quick Action Button */}
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/90 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800">
              <Zap className="h-4 w-4 animate-pulse text-emerald-600" />
              <span>Testing Purpose Only</span>
            </div>
            <span className="rounded-full bg-emerald-200/80 px-2 py-0.5 text-[10px] font-semibold text-emerald-900">
              Temporary Action
            </span>
          </div>
          <p className="mt-1 text-xs text-emerald-800 leading-snug">
            Click below to instantly authorize this mandate (status will change to <strong>ACTIVE</strong> with UMRN assigned) to test downstream flows.
          </p>
          <Button
            fullWidth
            type="button"
            loading={quickAuthorizing}
            onClick={handleQuickAuthorize}
            className="mt-3 h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs tracking-wide shadow-sm"
          >
            <CheckCircle className="mr-1.5 h-4 w-4" />
            ⚡ Quick Authorize Mandate (Test Mode)
          </Button>
        </div>

        {simulate ? (
          <div className="mt-4">
            <SimulationStep />
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col items-center text-center">
              <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-primary-50">
                <Landmark className="h-10 w-10 text-primary-500" />
              </div>
              <h1 className="mt-3 text-xl font-semibold text-ink">Authorise e-Mandate</h1>
              <p className="mt-1 text-[13px] text-ink-secondary">Reference: {refNo}</p>
            </div>

            <div className="mt-5 rounded-[12px] border border-line bg-card p-5 shadow-card">
              <div className="divide-y divide-slate-100">
                <Row label="Customer" value={mandate.customerName} />
                <Row label="Bank" value={`${mandate.bankName} · ${mandate.accountNumber}`} />
                <Row label="Amount" value={fmtINR(mandate.amount)} />
                <Row label="Maximum Amount" value={fmtINR(mandate.maxAmount)} />
                <Row label="Frequency" value={mandate.frequency} />
                <Row label="Valid From" value={mandate.startDate} />
                <Row label="Valid Till" value={mandate.endDate} />
                <Row label="Purpose" value={mandate.purpose} />
              </div>
            </div>

            <h2 className="mt-5 text-sm font-semibold text-ink">Select authorisation mode</h2>
            <div className="mt-3 flex flex-col gap-2.5">
              {MODES.map((m) => {
                const selected = mode === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMode(m.key)}
                    className={`flex h-16 items-center gap-3 rounded-[10px] border px-4 transition-colors cursor-pointer ${
                      selected
                        ? "border-primary-500 bg-primary-50"
                        : "border-slate-300 bg-white hover:border-slate-400"
                    }`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                      <m.icon className="h-5 w-5" />
                    </span>
                    <span className="flex-1 text-left">
                      <span className="block text-sm font-medium text-ink">{m.title}</span>
                      <span className="block text-xs text-ink-secondary">{m.sub}</span>
                    </span>
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        selected ? "border-primary-500" : "border-slate-300"
                      }`}
                    >
                      {selected && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                    </span>
                  </button>
                );
              })}
            </div>

            <Checkbox
              className="mt-4"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              label={`I authorise ${mandate.bankName} to debit my account as per the details shown above.`}
            />

            <Button
              fullWidth
              className="mt-4 h-11"
              disabled={!mode || !consent}
              loading={connecting}
              onClick={handleProceed}
            >
              {connecting ? "Connecting to bank..." : "Proceed to Authorise"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-[13px] text-ink-secondary">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}